import React, { useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { updateInfo } from "store/actions/authActions";

const PricingCard = ({
  user,
  uid,
  dispatch,
  chatRate,
  setChatRate,
  sportBadgeRate,
  setSportBadgeRate,
  sport,
  setSport,
  defaultSportProps,
  rvLoader,
  handleToggle,
  paypalEmail,
  setPaypalEmail,
}) => {
  const [isEditingPricing, setIsEditingPricing] = useState(false);
  const [saveLoader, setSaveLoader] = useState(false);

  const handleSavePricing = async (e) => {
    e.preventDefault();

    // Required fields: Biography and Profile Image must be present
    if (!user?.biography || !user.biography.trim()) {
      toast.error("Biography is required in Personal Information before saving pricing.");
      return;
    }
    if (!user?.player_image?.url) {
      toast.error("Profile Image is required before saving pricing.");
      return;
    }

    // Validate Chat Rate
    if (chatRate === "" || chatRate === undefined || chatRate === null) {
      toast.error("Chat Rate is required.");
      return;
    }
    if (Number(chatRate) < 0) {
      toast.error("Chat Rate cannot be negative.");
      return;
    }

    // Validate Sport Badge Rate
    if (sportBadgeRate === "" || sportBadgeRate === undefined || sportBadgeRate === null) {
      toast.error("Sport Badge Rate is required.");
      return;
    }
    if (Number(sportBadgeRate) < 0) {
      toast.error("Sport Badge Rate cannot be negative.");
      return;
    }

    // Validate PayPal Email
    if (!paypalEmail || !paypalEmail.trim()) {
      toast.error("PayPal Email is required.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(paypalEmail.trim())) {
      toast.error("Please enter a valid PayPal email.");
      return;
    }

    const updates = {};
    if (chatRate !== user?.chatRate) {
      updates.chatRate = chatRate;
    }
    if (sportBadgeRate !== (user?.sportBadgeRate || 4)) {
      updates.sportBadgeRate = sportBadgeRate;
    }
    if (sport !== user?.sport) {
      updates.sport = sport;
    }
    if (paypalEmail !== user?.paypal_email) {
      updates.paypal_email = paypalEmail.trim();
    }

    if (Object.keys(updates).length > 0) {
      setSaveLoader(true);
      try {
        await dispatch(updateInfo(uid, updates, "profile_fields"));
        setIsEditingPricing(false);
      } catch (err) {
        console.error(err);
        toast.error("Failed to update pricing and sport info.");
      } finally {
        setSaveLoader(false);
      }
    } else {
      setIsEditingPricing(false);
    }
  };

  return (
    <div className="saas-card">
      <div className="card-header-saas">
        <h3>Athlete Pricing & Sport</h3>
        {!isEditingPricing ? (
          <button
            className="saas-btn secondary sm"
            onClick={() => setIsEditingPricing(true)}
          >
            Edit
          </button>
        ) : (
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              className="saas-btn primary sm"
              onClick={handleSavePricing}
              disabled={
                saveLoader ||
                (chatRate === user?.chatRate &&
                  sportBadgeRate === (user?.sportBadgeRate || 4) &&
                  sport === user?.sport &&
                  paypalEmail === (user?.paypal_email || ""))
              }
            >
              {saveLoader ? <Spinner size="sm" /> : "Save"}
            </button>
            <button
              className="saas-btn secondary sm"
              onClick={() => {
                setChatRate(user?.chatRate || "");
                setSportBadgeRate(user?.sportBadgeRate || "");
                setSport(user?.sport || "");
                setPaypalEmail(user?.paypal_email || "");
                setIsEditingPricing(false);
              }}
              disabled={saveLoader}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <div className="info-field-group">
        {/* Chat Rate */}
        <div className="info-row">
          <span className="info-label">Chat Rate</span>
          {isEditingPricing ? (
            <input
              required
              min="0"
              className="saas-input"
              type="number"
              value={chatRate}
              onChange={(e) => setChatRate(e.target.value)}
              style={{ width: "60%", height: "32px", borderRadius: "6px", fontSize: "0.8125rem" }}
            />
          ) : (
            <span className="info-value">${user?.chatRate || "0"}</span>
          )}
        </div>

        {/* Sport Badge Rate */}
        <div className="info-row">
          <span className="info-label">Sport Badge Rate</span>
          {isEditingPricing ? (
            <input
              required
              min="0"
              className="saas-input"
              type="number"
              value={sportBadgeRate}
              onChange={(e) => setSportBadgeRate(e.target.value)}
              style={{ width: "60%", height: "32px", borderRadius: "6px", fontSize: "0.8125rem" }}
            />
          ) : (
            <span className="info-value">
              ${user?.sportBadgeRate !== undefined ? user.sportBadgeRate : "4"}
            </span>
          )}
        </div>

        {/* Sport selection */}
        <div className="info-row">
          <span className="info-label">Sport</span>
          {isEditingPricing ? (
            <div style={{ width: "60%" }}>
              <Autocomplete
                {...defaultSportProps}
                id="auto-complete-sport"
                autoComplete
                includeInputInList
                value={sport}
                onChange={(e, val) => setSport(val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    placeholder="Select Sport"
                    variant="standard"
                  />
                )}
              />
            </div>
          ) : (
            <span className="info-value">{user?.sport || "N/A"}</span>
          )}
        </div>

        {/* PayPal Email */}
        <div className="info-row">
          <span className="info-label">PayPal Email</span>
          {isEditingPricing ? (
            <input
              required
              className="saas-input"
              type="email"
              value={paypalEmail}
              onChange={(e) => setPaypalEmail(e.target.value)}
              style={{ width: "60%", height: "32px", borderRadius: "6px", fontSize: "0.8125rem" }}
            />
          ) : (
            <span className="info-value" style={{ wordBreak: "break-all" }}>
              {user?.paypal_email || "N/A"}
            </span>
          )}
        </div>

        {/* RV Rating adjustment */}
        <div className="info-row" style={{ borderTop: "1px solid #f1f5f9", paddingTop: "16px", marginTop: "8px", opacity: isEditingPricing ? 0.6 : 1 }}>
          <span className="info-label">RV Rating</span>
          <span className="info-value" style={{ fontWeight: 700, color: "#2563eb" }}>
            {user?.rvRating ? user.rvRating : "Pending"}
          </span>
        </div>

        {user?.rvRating && (
          <div className="info-row" style={{ opacity: isEditingPricing ? 0.6 : 1 }}>
            <span className="info-label">Show RV Rating</span>
            <div className="info-value d-flex justify-content-end">
              {rvLoader ? (
                <Spinner size="sm" />
              ) : (
                <div className="saas-switch" onClick={() => !isEditingPricing && handleToggle(user?.isShowRvRating)}>
                  <div className={`switch-track ${user?.isShowRvRating ? "active" : ""}`}>
                    <div className={`switch-thumb ${user?.isShowRvRating ? "active" : ""}`}></div>
                  </div>
                  <span>{user?.isShowRvRating ? "Yes" : "No"}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingCard;
